import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layers, Send, Loader2, Plus, X, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Textarea,
} from "@/components/ui";

interface FraccionDetalle {
  id: string;
  certificadoText: string | null;
  fraccionArancelaria: string;
  nico: string | null;
  consec: number;
}

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  fracciones: FraccionDetalle[];
  marca: string;
}

type TipoCambio = "AMPLIACION" | "REDUCCION" | "MIXTO";
type Accion = "ALTA" | "BAJA";

interface Movimiento {
  fraccion: string;
  accion: Accion;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const TAG_DOCS = "TELECOM_AMP_FRACCION";

/** Clave de comparación: trim + espacios colapsados + mayúsculas (espejo del backend). */
function normalizar(s: string): string {
  return s.trim().replace(/\s+/g, " ").toUpperCase();
}

/**
 * Pantalla dedicada de Ampliación / Reducción de Fracción Arancelaria, tercer
 * trámite de la familia "Ampliación / Reducción". Se llega desde la Selección de
 * Certificado con ?cert=<certificadoId>.
 *
 * El alcance vigente es la lista AUTORITATIVA de FRACCIONES ARANCELARIAS del
 * certificado (detalle.fracciones, con su NICO). La lista se precarga editable;
 * al enviar se calcula el delta contra el alcance original (lo agregado = ALTA,
 * lo quitado = BAJA) y se deriva el tipo de movimiento. El NICO se recaptura por
 * fracción en la emisión del certificado. El backend (crearAmpliacionFraccion)
 * recibe el contrato movimientos[]. El NICO se sigue capturando por fracción en
 * la emisión del certificado. La carga de documentos forma parte de la captura.
 */
export function AmpliacionFraccionAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [fracciones, setFracciones] = useState<string[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Envío en curso + solicitud creada (para reintento de subida sin re-crear).
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });

  // Alcance vigente de fracciones: la lista AUTORITATIVA del certificado
  // (códigos de fracción), base contra la que se calcula el delta.
  const base = useMemo(
    () => (qCert.data?.fracciones ?? []).map((f) => f.fraccionArancelaria),
    [qCert.data],
  );
  // NICO por fracción (solo para mostrar en el DICE; el NICO se recaptura en emisión).
  const nicoPorFraccion = useMemo(() => {
    const map = new Map<string, string>();
    (qCert.data?.fracciones ?? []).forEach((f) => {
      if (f.nico) map.set(normalizar(f.fraccionArancelaria), f.nico);
    });
    return map;
  }, [qCert.data]);

  // Precargar el editor con el alcance actual una sola vez.
  useEffect(() => {
    if (!seeded && qCert.isSuccess) {
      setFracciones(base);
      setSeeded(true);
    }
  }, [seeded, qCert.isSuccess, base]);

  // Delta calculado contra el alcance original → movimientos para el backend.
  const movimientos = useMemo<Movimiento[]>(() => {
    const baseKeys = new Set(base.map(normalizar));
    const actualKeys = new Set(fracciones.map(normalizar));
    const mv: Movimiento[] = [];
    fracciones.forEach((f) => {
      if (!baseKeys.has(normalizar(f))) mv.push({ fraccion: f, accion: "ALTA" });
    });
    base.forEach((f) => {
      if (!actualKeys.has(normalizar(f))) mv.push({ fraccion: f, accion: "BAJA" });
    });
    return mv;
  }, [fracciones, base]);

  const hayAlta = movimientos.some((m) => m.accion === "ALTA");
  const hayBaja = movimientos.some((m) => m.accion === "BAJA");
  const tipoMovimiento: TipoCambio | null =
    hayAlta && hayBaja ? "MIXTO" : hayAlta ? "AMPLIACION" : hayBaja ? "REDUCCION" : null;
  const hayCambios = movimientos.length > 0;

  const agregar = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    if (fracciones.some((f) => normalizar(f) === normalizar(t))) return; // dedup
    setFracciones((arr) => [...arr, t]);
  };
  const quitar = (fraccion: string) =>
    setFracciones((arr) => arr.filter((f) => normalizar(f) !== normalizar(fraccion)));

  const enviar = async () => {
    if (!hayCambios) {
      setError("No hay cambios en las fracciones respecto al alcance actual del certificado.");
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/ampliacion-fraccion", {
            certificadoBaseId: certId,
            tipoMovimiento,
            movimientos,
            motivo: motivo.trim() || null,
          })
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-fraccion" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const bloqueado = !!solicitudCreada;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=ampliacion-fraccion"
        backLabel="Cambiar certificado"
        icon={<Layers className="h-5 w-5" />}
        title="Ampliación / Reducción de Fracción Arancelaria"
        subtitle="Edita las fracciones arancelarias del alcance vigente; el sistema detecta automáticamente las altas y bajas."
      />

      <Alert variant="info">
        El resto del alcance (producto, modelos, normas, países) se conserva sin cambios. El NICO de cada fracción se captura en la emisión del certificado.
      </Alert>

      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={qCert.data.productoNombre} />
            <DataField label="Modelo principal" value={<span className="font-mono">{qCert.data.productoModelo}</span>} />
          </dl>
        )}
      </Card>

      {/* DICE: fracciones arancelarias actuales (solo lectura) */}
      <Card title="DICE — fracciones arancelarias actuales">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : base.length === 0 ? (
          <p className="text-sm text-fg-subtle">El certificado no tiene fracciones arancelarias registradas en su alcance.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {base.map((f) => {
              const nico = nicoPorFraccion.get(normalizar(f));
              return (
                <span key={normalizar(f)} className="rounded bg-surface-2 px-2 py-1 font-mono text-sm text-fg-muted">
                  {f}
                  {nico && <span className="ml-1 text-fg-faint">· NICO {nico}</span>}
                </span>
              );
            })}
          </div>
        )}
      </Card>

      {/* DEBE DECIR: editor de fracciones */}
      <Card title="DEBE DECIR — fracciones del nuevo alcance">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : (
          <FraccionesEditor fracciones={fracciones} onAgregar={agregar} onQuitar={quitar} disabled={bloqueado} />
        )}

        {/* Resumen de cambios detectados */}
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            Cambios detectados{hayCambios ? ` (${movimientos.length})` : ""}
          </div>
          {!hayCambios ? (
            <p className="mt-1 text-sm text-fg-subtle">
              Aún no hay cambios respecto al alcance actual. Agrega o quita fracciones arriba.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {movimientos.map((m) => (
                <span
                  key={`${m.accion}-${normalizar(m.fraccion)}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[11px] font-medium ${
                    m.accion === "ALTA" ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-err-bg text-estado-err"
                  }`}
                >
                  {m.accion === "ALTA" ? "+" : "−"} {m.fraccion}
                </span>
              ))}
            </div>
          )}
        </div>

        <FormField label="Justificación / comentarios" className="mt-4">
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            disabled={bloqueado}
            rows={3}
            placeholder="Explica el motivo del cambio de fracción arancelaria (reclasificación, nueva fracción/NICO, etc.)."
          />
        </FormField>
      </Card>

      {/* Documentos de soporte — parte de la captura */}
      <CapturaDocumentos
        tag={TAG_DOCS}
        titulo="Documentos de soporte"
        ayuda="Adjunta los documentos del trámite. Se subirán junto con la solicitud al enviarla."
        docs={docs}
        onChange={setDocs}
        disabled={enviando}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to="/telecom/servicios/seleccionar-certificado?s=ampliacion-fraccion">
          <Button variant="secondary" className="w-full sm:w-auto">Regresar</Button>
        </Link>
        {bloqueado ? (
          <>
            <Button variant="secondary" loading={enviando} onClick={enviar} className="w-full sm:w-auto">
              Reintentar documentos
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            icon={<Send className="h-4 w-4" />}
            loading={enviando}
            disabled={!hayCambios || qCert.isLoading || !!qCert.error}
            onClick={enviar}
            className="w-full sm:w-auto"
          >
            Enviar solicitud
          </Button>
        )}
      </div>
    </PageContainer>
  );
}

/** Editor de chips de fracciones (texto libre): input + agregar, lista con quitar. */
function FraccionesEditor({
  fracciones,
  onAgregar,
  onQuitar,
  disabled,
}: {
  fracciones: string[];
  onAgregar: (f: string) => void;
  onQuitar: (f: string) => void;
  disabled?: boolean;
}) {
  const [valor, setValor] = useState("");
  const commit = () => {
    onAgregar(valor);
    setValor("");
  };
  return (
    <div className="space-y-3">
      <FormField label="Agregar fracción al alcance">
        <div className="flex gap-2">
          <Input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            disabled={disabled}
            placeholder="Escribe una fracción y pulsa Enter o Agregar"
            className="font-mono"
          />
          <Button
            type="button"
            variant="secondary"
            icon={<Plus className="h-4 w-4" />}
            onClick={commit}
            disabled={disabled || !valor.trim()}
          >
            Agregar
          </Button>
        </div>
      </FormField>

      {fracciones.length === 0 ? (
        <p className="text-sm text-fg-subtle">Sin fracciones arancelarias en el alcance.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {fracciones.map((f) => (
            <span
              key={normalizar(f)}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary-strong/40 bg-primary-tint px-2.5 py-1 font-mono text-sm text-primary-strong"
            >
              {f}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onQuitar(f)}
                  className="cursor-pointer rounded-full p-0.5 text-primary-strong/60 transition hover:bg-primary-tint hover:text-primary-strong"
                  aria-label={`Quitar ${f}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
